import {
  BookMarked,
  CircleAlert,
  CircleCheck,
  ListChecks,
  Route,
  Search,
  ShieldAlert,
} from "lucide-react";

import type { JournalProfileMatch } from "../domain/journal-profiles";

type JournalProfileMatcherProps = {
  articleType: string;
  journal: string;
  match: JournalProfileMatch;
};

function Checklist({
  icon,
  items,
  label,
  open,
  tone = "neutral",
}: {
  icon: React.ReactNode;
  items: string[];
  label: string;
  open?: boolean;
  tone?: "neutral" | "warning";
}) {
  if (!items.length) return null;
  return (
    <details className={`journal-checklist ${tone}`} open={open}>
      <summary>
        {icon}
        <span>{label}</span>
        <small>{items.length} 项</small>
      </summary>
      <ul>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </details>
  );
}

export function JournalProfileMatcher({
  articleType,
  journal,
  match,
}: JournalProfileMatcherProps) {
  if (!journal.trim()) {
    return (
      <section className="journal-match-card idle" aria-live="polite">
        <div className="journal-match-empty-icon">
          <Search size={18} />
        </div>
        <div>
          <strong>输入期刊名后自动匹配</strong>
          <p>
            支持 PLOS ONE、Nature Communications、Elsevier、BMC 等常见名称；不会自动把参考配置标成官网已核验。
          </p>
        </div>
      </section>
    );
  }

  if (!match.matched) {
    return (
      <section className="journal-match-card unmatched" aria-live="polite">
        <div className="journal-match-heading">
          <div className="journal-match-title">
            <CircleAlert size={18} />
            <div>
              <span>未命中内置期刊配置</span>
              <strong>不会根据相似名称猜测</strong>
            </div>
          </div>
          <span className="journal-status-chip unknown">待人工核对</span>
        </div>
        <p className="journal-match-notice">
          请确认期刊全名{articleType.trim() ? "" : "，并补充文章类型"}；随后应检查该期刊官网的 Instructions for Authors、当前模板、参考文献与声明要求。
        </p>
      </section>
    );
  }

  const sourceLabel =
    match.matchSource === "high-frequency" ? "高频期刊清单" : "出版社通用配置";

  return (
    <section className="journal-match-card matched" aria-live="polite">
      <div className="journal-match-heading">
        <div className="journal-match-title">
          <CircleCheck size={18} />
          <div>
            <span>已自动匹配 · {sourceLabel}</span>
            <strong>{match.displayName}</strong>
          </div>
        </div>
        <span className="journal-status-chip reference">内置参考 · 未重新核验</span>
      </div>

      <dl className="journal-match-facts">
        <div>
          <dt>匹配依据</dt>
          <dd>{match.matchedAlias}</dd>
        </div>
        <div>
          <dt>模板路线</dt>
          <dd>{match.templateFamily || "按期刊官网选择"}</dd>
        </div>
        <div>
          <dt>文章类型</dt>
          <dd>{articleType.trim() || "尚未填写，需补充后核验"}</dd>
        </div>
        <div>
          <dt>配置版本</dt>
          <dd>{match.registryVersion}</dd>
        </div>
      </dl>

      <div className="journal-verification-banner">
        <ShieldAlert size={16} />
        <span>
          已载入模板路线和检查清单；最终套用前仍须按“准确期刊 + 文章类型”核对当前官网。
        </span>
      </div>

      <div className="journal-checklists">
        <Checklist
          icon={<BookMarked size={15} />}
          items={match.knownLikelyRequirements}
          label="常见要求（先作参考）"
          open
        />
        <Checklist
          icon={<Search size={15} />}
          items={match.officialSourcesToCheck}
          label="官网必须核对"
        />
        <Checklist
          icon={<Route size={15} />}
          items={match.stage5OverridePoints}
          label="最终版式差异点"
        />
        <Checklist
          icon={<ListChecks size={15} />}
          items={match.mustNotAssume}
          label="绝不能直接假设"
          tone="warning"
        />
      </div>
    </section>
  );
}
